import React from 'react';

import { CommonRoute } from '@navigation/Routes';
import type { ViewFollowsScreenToggleState, ViewPlanScreenToggleState } from '@navigation/Types';
import { createStackNavigator } from '@react-navigation/stack';
import ChatScreen from '@screens/Chat/Chat';
import ChatThreadsScreen from '@screens/Chat/Threads';
import { CreateGroupScreen, UpdateGroupScreen } from '@screens/Group/Form';
import ViewGroupMembersScreen from '@screens/Group/Members';
import ViewGroupScreen from '@screens/Group/View';
import InviteContactsScreen from '@screens/InviteContacts';
import NotificationsScreen from '@screens/Notifications';
import { UpdatePlanScreen } from '@screens/Plan/Update';
import ViewPlanScreen from '@screens/Plan/View';
import SettingsScreen from '@screens/Settings';
import { UpdateUserScreen } from '@screens/User/Form';
import ProfileScreen, { CurrentUserProfileScreen } from '@screens/User/Profile';
import ViewFollowsScreen from '@screens/User/ViewFollows';

export type CommonStackParamList = {
  [CommonRoute.CurrentUserProfile]: undefined;
  [CommonRoute.Profile]: {
    userId: string;
  };
  [CommonRoute.ViewPlan]: {
    planId: string;
    toggleState?: ViewPlanScreenToggleState;
    reload?: boolean;
  };
  [CommonRoute.Notifications]: undefined;
  [CommonRoute.ChatThreads]: undefined;
  [CommonRoute.Chat]: {
    chatId: string;
  };
  [CommonRoute.CreateChat]: undefined;
  [CommonRoute.UpdatePlan]: {
    planId: string;
  };
  [CommonRoute.ViewFollows]: {
    userId: string;
    userDisplayName?: string;
    toggleState?: ViewFollowsScreenToggleState;
  };
  [CommonRoute.ViewGroup]: {
    groupId: string;
    reload?: boolean;
  };
  [CommonRoute.ViewGroupMembers]: {
    groupId: string;
  };
  [CommonRoute.UpdateGroup]: {
    groupId: string;
  };
  [CommonRoute.CreateGroup]: undefined;
  [CommonRoute.InviteContacts]: undefined;
  [CommonRoute.Settings]: undefined;
  [CommonRoute.UpdateUser]: undefined;
};

type CommonPushStackNavigator<ParamList extends CommonStackParamList> = ReturnType<
  typeof createStackNavigator<ParamList>
>;
type CommonScreenComponent = React.ComponentType<Record<never, never>>;

export default function getCommonStack<ParamList extends CommonStackParamList>(
  Stack: CommonPushStackNavigator<ParamList>
) {
  return getCommonPushScreens(Stack);
}

function getCommonPushScreens<ParamList extends CommonStackParamList>(
  Stack: CommonPushStackNavigator<ParamList>
) {
  return (
    <>
      <Stack.Screen
        name={CommonRoute.CurrentUserProfile}
        component={getCommonScreen(CurrentUserProfileScreen)}
        options={{ title: 'You' }}
      />
      <Stack.Screen
        name={CommonRoute.Profile}
        component={getCommonScreen(ProfileScreen)}
        options={{ title: 'Profile' }}
      />
      <Stack.Screen
        name={CommonRoute.ViewPlan}
        component={getCommonScreen(ViewPlanScreen)}
        options={{ title: 'Plan' }}
      />
      <Stack.Screen
        name={CommonRoute.Notifications}
        component={getCommonScreen(NotificationsScreen)}
        options={{ title: 'Notifications' }}
      />
      <Stack.Screen
        name={CommonRoute.ChatThreads}
        component={getCommonScreen(ChatThreadsScreen)}
        options={{ title: 'Messages' }}
      />
      <Stack.Screen
        name={CommonRoute.Chat}
        component={getCommonScreen(ChatScreen)}
        options={{ title: 'Chat' }}
      />
      <Stack.Screen
        name={CommonRoute.CreateChat}
        component={getCommonScreen(ChatScreen)}
        options={{ title: 'New Message' }}
      />
      <Stack.Screen
        name={CommonRoute.UpdatePlan}
        component={getCommonScreen(UpdatePlanScreen)}
        options={{ title: 'Edit Plan' }}
      />
      <Stack.Screen
        name={CommonRoute.ViewFollows}
        component={getCommonScreen(ViewFollowsScreen)}
        options={{ title: 'Follows' }}
      />
      <Stack.Screen
        name={CommonRoute.ViewGroup}
        component={getCommonScreen(ViewGroupScreen)}
        options={{ title: 'Group' }}
      />
      <Stack.Screen
        name={CommonRoute.ViewGroupMembers}
        component={getCommonScreen(ViewGroupMembersScreen)}
        options={{ title: 'Members' }}
      />
      <Stack.Screen
        name={CommonRoute.UpdateGroup}
        component={getCommonScreen(UpdateGroupScreen)}
        options={{ title: 'Update Group' }}
      />
      <Stack.Screen
        name={CommonRoute.CreateGroup}
        component={getCommonScreen(CreateGroupScreen)}
        options={{ title: 'Create Group' }}
      />
      <Stack.Screen
        name={CommonRoute.InviteContacts}
        component={getCommonScreen(InviteContactsScreen)}
        options={{ title: 'Invite Contacts' }}
      />
      <Stack.Screen
        name={CommonRoute.UpdateUser}
        component={getCommonScreen(UpdateUserScreen)}
        options={{ title: 'Update Profile' }}
      />
      <Stack.Screen
        name={CommonRoute.Settings}
        component={getCommonScreen(SettingsScreen)}
        options={{ title: 'Settings' }}
      />
    </>
  );
}

function getCommonScreen<TProps extends object>(
  Component: React.ComponentType<TProps>
): CommonScreenComponent {
  return Component as CommonScreenComponent;
}
